<script>
/* eslint-disable */
/**
 * m-table —— 手机端卡片式表格
 *
 * 直接复用页面里 iView <Table> 的 columns 定义与 data，
 * 把每一行渲染成一张卡片：
 *   - type: 'selection' 的列 -> 卡片左上角复选框
 *   - type: 'index'     的列 -> 卡片左上角序号
 *   - 操作列（title 为“操作”/key 为 Button/action，或显式 mobileAction: true）
 *     -> 卡片底部操作区，直接调用原 render(h, params)，行为与 PC 端一致
 *   - 其余列 -> 卡片内“标签 / 值”一行，值同样优先走原 render 或具名插槽
 *
 * 列上可选的手机端扩展字段（不影响 PC 端 Table）：
 *   mobileHidden: true   该列在手机端不展示
 *   mobileTitle: true    该列作为卡片标题
 *   mobileAction: true   该列强制作为底部操作区
 *   mobileSpan: true     该列的值独占一行（长文本/图片）
 */
export default {
    name: 'MTable',
    props: {
        // iView Table 的 columns，原样传入即可
        columns: { type: Array, default: () => [] },
        // 表格数据
        data: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        // 行唯一标识字段
        rowKey: { type: String, default: 'Id' },
        // 空数据文案
        emptyText: { type: String, default: '暂无数据' },
        // 是否允许点击整卡触发 on-row-click
        clickable: { type: Boolean, default: false },
    },
    data() {
        return {
            // 已选中的行（对象引用）
            selected: [],
        };
    },
    computed: {
        // 有效列：过滤掉手机端隐藏列
        visibleColumns() {
            return (this.columns || []).filter((c) => c && !c.mobileHidden);
        },
        selectionColumn() {
            return this.visibleColumns.find((c) => c.type === 'selection');
        },
        indexColumn() {
            return this.visibleColumns.find((c) => c.type === 'index');
        },
        actionColumns() {
            return this.visibleColumns.filter((c) => this.isActionColumn(c));
        },
        titleColumn() {
            const explicit = this.visibleColumns.find((c) => c.mobileTitle);
            if (explicit) return explicit;
            const dataCols = this.visibleColumns.filter((c) => this.isDataColumn(c));
            // 优先挑“名称”类字段当卡片标题，否则第一个数据列会是库存、排序码这种没有辨识度的值
            const named = dataCols.find((c) => {
                const t = c.title || '';
                const k = c.key || '';
                return /名称|名字|姓名|标题|昵称|单号|编号|内容/.test(t) || /name|title|nick/i.test(k);
            });
            return named || dataCols[0];
        },
        // 卡片主体展示的列（去掉标题列、操作列、selection/index）
        bodyColumns() {
            const title = this.titleColumn;
            return this.visibleColumns.filter(
                (c) => this.isDataColumn(c) && c !== title
            );
        },
        hasSelection() {
            return !!this.selectionColumn;
        },
        allSelected() {
            return (
                this.data.length > 0 && this.selected.length === this.data.length
            );
        },
    },
    mounted() {
        this.markHost();
    },
    updated() {
        this.markHost();
    },
    watch: {
        // 数据刷新后清空选中，避免选中已不存在的行
        data() {
            if (this.selected.length) {
                this.selected = [];
                this.emitSelection();
            }
        },
    },
    methods: {
        // 手机端卡片列表外层若是 iView Card，给它打个标记，
        // 由样式把白色面板变透明，卡片才有分隔感（见 styles/mobile.less）
        markHost() {
            const el = this.$el;
            if (!el || typeof el.closest !== 'function') return;
            const card = el.closest('.ivu-card');
            if (card) card.classList.add('has-m-table');
        },
        isActionColumn(c) {
            if (c.mobileAction) return true;
            if (c.type === 'selection' || c.type === 'index') return false;
            const key = (c.key || '').toLowerCase();
            return (
                c.title === '操作' ||
                c.title === '操作栏' ||
                key === 'button' ||
                key === 'action' ||
                key === 'handle' ||
                key === 'operation'
            );
        },
        // 图片、长文本类字段在卡片里独占一行，避免挤成两列后换行成一团
        isSpanColumn(c) {
            if (c.mobileSpan) return true;
            const t = c.title || '';
            return /图|封面|头像|描述|备注|地址|内容|详情|链接|说明|规格/.test(t);
        },
        isDataColumn(c) {
            return (
                c.type !== 'selection' &&
                c.type !== 'index' &&
                c.type !== 'expand' &&
                !this.isActionColumn(c)
            );
        },
        // 渲染单元格内容：render > 具名插槽 > 原始值
        renderCell(h, column, row, index) {
            const params = { row, index, column };
            if (typeof column.render === 'function') {
                return column.render(h, params);
            }
            if (column.slot && this.$scopedSlots[column.slot]) {
                return this.$scopedSlots[column.slot](params);
            }
            const val = column.key ? row[column.key] : '';
            if (val === null || val === undefined || val === '') return '—';
            return String(val);
        },
        isSelected(row) {
            return this.selected.indexOf(row) !== -1;
        },
        toggleSelect(row) {
            const i = this.selected.indexOf(row);
            if (i === -1) {
                this.selected.push(row);
                this.$emit('on-select', this.selected.slice(), row);
            } else {
                this.selected.splice(i, 1);
                this.$emit('on-select-cancel', this.selected.slice(), row);
            }
            this.emitSelection();
        },
        toggleSelectAll() {
            this.selectAll(!this.allSelected);
        },
        emitSelection() {
            // 与 iView Table 的 on-selection-change 事件保持一致
            this.$emit('on-selection-change', this.selected.slice());
        },
        // 供父组件调用，与 iView Table 的 selectAll 语义一致
        selectAll(status) {
            this.selected = status ? this.data.slice() : [];
            if (status) this.$emit('on-select-all', this.selected.slice());
            else this.$emit('on-select-all-cancel', this.selected.slice());
            this.emitSelection();
        },
        clearSelection() {
            this.selected = [];
            this.emitSelection();
        },
        // 与 iView Table 接口对齐的空实现，避免页面调用时报错
        exportCsv() {},
        renderCard(h, row, index) {
            const children = [];

            // ---- 卡片头部：复选框 / 序号 / 标题 ----
            const head = [];
            if (this.hasSelection) {
                head.push(
                    h('div', {
                        class: ['m-card__check', { 'is-checked': this.isSelected(row) }],
                        on: {
                            click: (e) => {
                                e.stopPropagation();
                                this.toggleSelect(row);
                            },
                        },
                    })
                );
            }
            if (this.indexColumn) {
                head.push(h('span', { class: 'm-card__index' }, String(index + 1)));
            }
            if (this.titleColumn) {
                head.push(
                    h('div', { class: 'm-card__title' }, [
                        this.renderCell(h, this.titleColumn, row, index),
                    ])
                );
            }
            if (head.length) {
                children.push(h('div', { class: 'm-card__head' }, head));
            }

            // ---- 卡片主体：标签 / 值 ----
            const cells = this.bodyColumns.map((c) =>
                h('div', { class: ['m-card__cell', { 'is-span': this.isSpanColumn(c) }] }, [
                    h('span', { class: 'm-card__label' }, c.title || ''),
                    h('span', { class: 'm-card__value' }, [
                        this.renderCell(h, c, row, index),
                    ]),
                ])
            );
            if (cells.length) {
                children.push(h('div', { class: 'm-card__body' }, cells));
            }

            // ---- 卡片底部：操作区 ----
            const actions = this.actionColumns.map((c) =>
                h('div', { class: 'm-card__action' }, [this.renderCell(h, c, row, index)])
            );
            if (actions.length) {
                children.push(h('div', { class: 'm-card__foot' }, actions));
            }

            return h(
                'div',
                {
                    class: ['m-card', { 'is-clickable': this.clickable }],
                    key: (row && row[this.rowKey]) || index,
                    on: {
                        click: () => {
                            if (this.clickable) this.$emit('on-row-click', row, index);
                        },
                    },
                },
                children
            );
        },
    },
    render(h) {
        const children = [];

        // 全选条（有 selection 列时才出现）
        if (this.hasSelection && this.data.length) {
            children.push(
                h('div', { class: 'm-table__bulk' }, [
                    h('div', {
                        class: ['m-card__check', { 'is-checked': this.allSelected }],
                        on: { click: this.toggleSelectAll },
                    }),
                    h('span', { class: 'm-table__bulk-text' }, this.allSelected ? '取消全选' : '全选'),
                    h(
                        'span',
                        { class: 'm-table__bulk-count' },
                        this.selected.length ? '已选 ' + this.selected.length + ' 项' : ''
                    ),
                ])
            );
        }

        if (this.loading && !this.data.length) {
            children.push(h('div', { class: 'm-table__empty' }, '加载中…'));
        } else if (!this.data.length) {
            children.push(h('div', { class: 'm-table__empty' }, this.emptyText));
        } else {
            this.data.forEach((row, i) => {
                children.push(this.renderCard(h, row, i));
            });
            if (this.loading) {
                children.push(h('div', { class: 'm-table__mask' }));
            }
        }

        return h('div', { class: 'm-table' }, children);
    },
};
</script>
